import { useMemo } from 'react';
import {
  ChartConfig,
  ChartContainer,
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer
} from '@gettactic/components/src/@v2/components/ui/chart';
import {
  ChartTooltip,
  ChartTooltipContent
} from '@gettactic/components/src/@v2/components/ui/chart';
import { ResGetUser } from '@gettactic/api';
import { NoDataMessage } from './ChartNoDataMessage';
import { useColumnWidth, columnXSpacing } from '@gettactic/components/src/contexts/ColumnWidthContext';

export const WeeklyChart = ({
  isLoading,
  chartInfo: { chartData, chartConfig },
  myId,
  userCount,
  daysCount
}: {
  isLoading: boolean;
  chartInfo: {
    chartData: {
      name: string;
      inOffice: number;
      favorite: number;
    }[];
    chartConfig: ChartConfig;
  };
  myId: ResGetUser['id'];
  userCount: number;
  daysCount: number;
}) => {
  const noOneInTheOffice = useMemo(() => userCount < 1, [userCount]);
  const { dayColumnWidth } = useColumnWidth();
  
  // Custom shape renderer for inOffice bars - checks if favorite exists for this day
  const InOfficeShape = (props: any) => {
    const { x, y, width, height, index } = props;
    const dataPoint = chartData[index];
    const hasFavorite = dataPoint && dataPoint.favorite > 0;
    
    // Use rounded corners only when favorite doesn't exist for this data point
    const radius = hasFavorite ? [0, 0, 4, 4] : [4, 4, 4, 4];
    const [topLeft, topRight, bottomRight, bottomLeft] = radius;
    
    if (height === 0) return null;
    
    // Ensure the shape uses the full container width by not subtracting from edges
    return (
      <path
        d={`
          M ${x},${y + topLeft}
          Q ${x},${y} ${x + topLeft},${y}
          L ${x + width - topRight},${y}
          Q ${x + width},${y} ${x + width},${y + topRight}
          L ${x + width},${y + height - bottomRight}
          Q ${x + width},${y + height} ${x + width - bottomRight},${y + height}
          L ${x + bottomLeft},${y + height}
          Q ${x},${y + height} ${x},${y + height - bottomLeft}
          Z
        `}
        fill="var(--color-inOffice)"
      />
    );
  };
  
  // Custom shape renderer for favorite bars - checks if inOffice exists for this day
  const FavoriteShape = (props: any) => {
    const { x, y, width, height, index } = props;
    const dataPoint = chartData[index];
    const hasInOffice = dataPoint && dataPoint.inOffice > 0;
    
    // Use rounded corners only when inOffice doesn't exist for this data point
    const radius = hasInOffice ? [4, 4, 0, 0] : [4, 4, 4, 4];
    const [topLeft, topRight, bottomRight, bottomLeft] = radius;
    
    if (height === 0) return null;
    
    // Ensure the shape uses the full container width by not subtracting from edges
    return (
      <path
        d={`
          M ${x},${y + topLeft}
          Q ${x},${y} ${x + topLeft},${y}
          L ${x + width - topRight},${y}
          Q ${x + width},${y} ${x + width},${y + topRight}
          L ${x + width},${y + height - bottomRight}
          Q ${x + width},${y + height} ${x + width - bottomRight},${y + height}
          L ${x + bottomLeft},${y + height}
          Q ${x},${y + height} ${x},${y + height - bottomLeft}
          Z
        `}
        fill="var(--color-favorite)"
      />
    );
  };

  return (
    <div className="w-full" style={{ padding: 0, margin: 0 }}>
      <ResponsiveContainer width="100%" height={100}>
        {userCount === 0 && noOneInTheOffice ? (
          <NoDataMessage myId={myId} />
        ) : (
          <ChartContainer config={chartConfig} className="w-full">
            <BarChart 
              accessibilityLayer 
              data={chartData.slice(0, daysCount)}
              barCategoryGap={columnXSpacing}
              barGap={0}
              margin={{ top: 0, right: 0, bottom: 0, left: 0 }}
              maxBarSize={100}
            >
              <CartesianGrid vertical={false} />
              <ChartTooltip content={<ChartTooltipContent className="text-white" hideLabel />} />
              <Bar
                dataKey="inOffice"
                stackId="a"
                shape={<InOfficeShape />}
                barSize={dayColumnWidth}
                maxBarSize={dayColumnWidth}
              />
              <Bar
                dataKey="favorite"
                stackId="a"
                shape={<FavoriteShape />}
                barSize={dayColumnWidth}
                maxBarSize={dayColumnWidth}
              />
            </BarChart>
          </ChartContainer>
        )}
      </ResponsiveContainer>
    </div>
  );
};
